import React, { useMemo } from "react";
import { formatTimeInTZ } from "../../lib/timezone";

// =============================================================================
// OUTBOUND LOG VIEW
// =============================================================================
// PURPOSE: Chronological log of a setter's outbound (setter-sent) DM messages
// for a single window, with repeat-text detection and burst grouping — surfaces
// canned-message blasts that a per-conversation chat-bubble view can't show.
//
// SIGNALS:
// - Repeat badge: same normalized text sent more than once in the window.
// - Burst header: 3+ messages within a rolling 60s window, tagged with how many
//   distinct conversations received them — separates "busy setter replying to
//   several leads at once" from "canned text blasted to a list".
//
// KNOWN RESIDUAL (accepted, not a bug): normalization still misses per-lead
// personalization ("hey Sarah" vs "hey Mike" don't match). A fuzzy near-duplicate
// pass was considered and rejected for v1 — an untuned threshold makes the badge
// worse than absent. The burst header catches the blast pattern independently of
// text, so the two signals cover each other.
// =============================================================================

export interface OutboundMessage {
  id: string;
  sent_at: string;
  message_text: string | null;
  has_media: boolean;
  conversation_id: string;
  contact_name: string | null;
  lead_instagram: string | null;
  /** Minutes this lead waited for THIS message, or null when the send answered
   *  nothing (cold opener, follow-up into silence, 2nd of a double-text).
   *  Populated by attachReplyLags in SessionMessagesModal.fetchOutbound —
   *  roughly half of all rows are legitimately null. Optional so the 5 existing
   *  Vitest fixtures still typecheck untouched. */
  reply_lag_mins?: number | null;
}

interface OutboundLogViewProps {
  messages: OutboundMessage[]; // pre-sorted ascending by sent_at
  timezone: string;
  /** Sessions the day-row recorded. Drives the reassignment-aware empty state. REQUIRED, not optional. */
  sessionCount: number;
  onConversationClick?: (
    conversationId: string,
    contactName: string,
    leadInstagram: string | null
  ) => void;
}

// Punctuation-insensitive normalization for repeat detection. A plain
// lowercase-collapse treats "hey!" and "hey" as different strings, so a single
// emoji or exclamation point would defeat the badge. Keep module-private.
const norm = (t: string | null) =>
  (t ?? "")
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, "")
    .split(/\s+/)
    .filter(Boolean)
    .join(" ");

const hasText = (m: OutboundMessage) =>
  m.message_text !== null && m.message_text !== "" && !m.message_text.startsWith("Opportunity");

/**
 * Renders a reply lag for the Timeline badge.
 *
 * ⚠️ THE PARAMETER IS MINUTES AND MUST STAY MINUTES. Sub-minute lags render as
 * real seconds (Mako's Aug 6 2026 ruling: "43s better that's great i like
 * that"), and the obvious way to implement that is to flip this parameter to
 * seconds. That is the wrong fix and it fails SILENTLY. formatLag is called
 * minutes-in at six sites across this feature and its siblings, so a flipped
 * parameter renders every one of them 60x too small — a 14-minute gap prints
 * "14s". Both units are `number`, so typecheck passes clean and only a test
 * asserting an exact string catches it. The sub-60 branch below gets seconds
 * on screen without touching a single call site.
 *
 * The two guards are not decoration. !Number.isFinite catches the NaN a median
 * over an empty array produces (0/0 is a valid number, and without this every
 * branch below is false and the UI renders the literal string "NaNh NaNm").
 * mins < 0 catches clock skew between two rows, which would otherwise print
 * "-5s" or get silently Math.abs()'d into a plausible wrong number.
 */
// formatLag is pinned to this file on purpose: P-TIMELINE-IDLE-GAP and
// P-REPLYTIME-RECONCILE both import it from here, and the unit tests call it
// directly. Moving it to its own module to satisfy Fast Refresh would fork the
// 60x guard away from the badge it protects. Cost is HMR on this one file.
// eslint-disable-next-line react-refresh/only-export-components
export const formatLag = (mins: number): string => {
  if (!Number.isFinite(mins) || mins < 0) return "—";
  const secs = Math.round(mins * 60); // round FIRST, then branch
  if (secs < 60) return `${secs}s`;
  if (mins < 60) return `${Math.round(mins)}m`;
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
};

export interface LagInputMsg {
  id: string;
  conversation_id: string;
  sent_at: string;
  sender_type: string;
  ghl_sender_user_id: string | null;
  /**
   * OPTIONAL, and the caller deliberately does NOT select it.
   *
   * attachReplyLags below has no text predicate — the one that briefly existed
   * was measured to catch zero activity rows and was removed. Fetching the
   * bodies anyway cost 149 KB of a 407 KB lag download (37%), parsed and
   * discarded, on every day-detail modal open. See the select in
   * SessionMessagesModal.fetchOutbound.
   *
   * Kept on the type rather than deleted so the existing unit-test fixtures in
   * __tests__/OutboundLogView.test.ts still compile unchanged. If you find
   * yourself wanting to read it here, you are adding a text predicate — go
   * re-read why the last one was removed first.
   */
  message_text?: string | null;
}

/**
 * Attaches reply_lag_mins to each outbound message.
 *
 * `context` = every message in those conversations, BOTH sides, any order.
 * Exported as a pure function so it is unit-testable in a .test.ts with no DOM
 * (vitest here is environment:"node" — no jsdom, no @testing-library).
 */
// Same reason as formatLag above. SessionMessagesModal.fetchOutbound imports
// this, and the unit tests call it with no DOM.
// eslint-disable-next-line react-refresh/only-export-components
export function attachReplyLags(
  outbound: OutboundMessage[],
  context: LagInputMsg[]
): OutboundMessage[] {
  const isHumanSetter = (m: LagInputMsg) =>
    m.sender_type === "setter" && m.ghl_sender_user_id !== null; // CLAUDE.md #6

  // NO text filter and NO type filter here, deliberately.
  // INVARIANT THIS RELIES ON: the caller's query carries
  // .eq("message_type","TYPE_INSTAGRAM"), so every row reaching this function is
  // already an Instagram message — no activity row, no TYPE_CALL, no internal
  // comment can be present. That server-side allowlist is what makes a JS type
  // check unnecessary. IF THAT FILTER IS EVER LOOSENED, THIS PREDICATE MUST
  // GAIN A TYPE CHECK.
  // A text filter on "Opportunity" was tried and removed: measured live it
  // caught ZERO activity rows (all 74 are TYPE_ACTIVITY_OPPORTUNITY, already
  // excluded server-side and all sender_type='setter' anyway) and removed 3
  // GENUINE lead messages that happen to open with that word.
  const isLeadSpeaking = (m: LagInputMsg) => m.sender_type !== "setter";

  const byConvo = new Map<string, LagInputMsg[]>();
  for (const m of context) {
    const arr = byConvo.get(m.conversation_id) ?? [];
    arr.push(m);
    byConvo.set(m.conversation_id, arr);
  }

  const lagById = new Map<string, number>();
  for (const msgs of byConvo.values()) {
    // context arrives pre-sorted; the stable re-sort is cheap insurance
    msgs.sort((a, b) => a.sent_at.localeCompare(b.sent_at));
    let lastLeadAt: number | null = null;
    for (const m of msgs) {
      // A lead's 3-in-a-row collapses to the LAST one (set, not set-if-null):
      // the lag measures from their most recent ping, which is what "how long
      // did they wait" means.
      if (isLeadSpeaking(m)) {
        lastLeadAt = new Date(m.sent_at).getTime();
        continue;
      }
      // ManyChat (null sender) neither speaks nor clears. Treating it as a
      // setter reply would clear a real lead's pending timestamp; treating it
      // as a lead would invent one.
      if (!isHumanSetter(m)) continue;
      if (lastLeadAt !== null) {
        lagById.set(m.id, (new Date(m.sent_at).getTime() - lastLeadAt) / 60000);
      }
      // THE RESET — runs whether or not a lag was emitted. This is what makes
      // only the FIRST message of a double-text carry a badge. Drop it and
      // every message in a burst inherits the same stale lead timestamp.
      lastLeadAt = null;
    }
  }

  return outbound.map((m) => ({ ...m, reply_lag_mins: lagById.get(m.id) ?? null }));
}

interface Burst {
  start: number;
  end: number;
} // inclusive indices into `messages`

export const OutboundLogView: React.FC<OutboundLogViewProps> = ({
  messages,
  timezone,
  sessionCount,
  onConversationClick,
}) => {
  const stats = useMemo(() => {
    const total = messages.length;
    const withText = messages.filter(hasText).length;
    const mediaOnly = messages.filter((m) => !hasText(m) && m.has_media).length;
    const convos = new Set(messages.map((m) => m.conversation_id)).size;

    // Empty normalized text NEVER counts as a repeat — every media-only send
    // would otherwise read as a duplicate of every other media-only send.
    const counts = new Map<string, number>();
    for (const m of messages) {
      const k = norm(m.message_text);
      if (k) counts.set(k, (counts.get(k) ?? 0) + 1);
    }
    const isRepeat = (m: OutboundMessage) => (counts.get(norm(m.message_text)) ?? 0) > 1;
    const repeats = messages.filter(isRepeat).length;

    // `fast` (10s) measures send cadence. The first message has no previous
    // send, so it's excluded from the numerator but stays in the denominator
    // (`total`) — one send in a day is 0% fast, not 100%.
    let fast = 0;
    for (let i = 1; i < messages.length; i++) {
      if (+new Date(messages[i].sent_at) - +new Date(messages[i - 1].sent_at) < 10_000) fast++;
    }

    const pct = (x: number) => (total === 0 ? 0 : Math.round((x / total) * 100));
    const repeatPct = pct(repeats);
    const fastPct = pct(fast);

    // Bursts (60s window) group a visible block for the header — a DIFFERENT
    // threshold from `fast` on purpose: one measures cadence, the other groups
    // a contiguous run of sends into a single displayed unit.
    const bursts: Burst[] = [];
    for (let i = 0; i < messages.length; ) {
      let j = i;
      while (
        j + 1 < messages.length &&
        +new Date(messages[j + 1].sent_at) - +new Date(messages[j].sent_at) < 60_000
      ) {
        j++;
      }
      if (j - i + 1 >= 3) bursts.push({ start: i, end: j });
      i = j + 1;
    }
    const burstByStart = new Map<number, Burst>();
    for (const b of bursts) burstByStart.set(b.start, b);

    const GAP_S = 240; // matches sql/get_setter_sessions.sql:78 — do NOT retune independently
    const gapByIndex = new Map<number, number>(); // index i -> gap in MINUTES
    let gapCount = 0,
      gapTotalMins = 0,
      clockedMins = 0;
    for (let i = 1; i < messages.length; i++) {
      const secs = (+new Date(messages[i].sent_at) - +new Date(messages[i - 1].sent_at)) / 1000;
      if (secs > GAP_S) {
        gapByIndex.set(i, secs / 60);
        gapCount++;
        gapTotalMins += secs / 60;
      } else {
        clockedMins += secs / 60;
      }
    }
    const spanMins =
      messages.length > 1
        ? (+new Date(messages[messages.length - 1].sent_at) - +new Date(messages[0].sent_at)) /
          60000
        : 0;

    // median of the no-activity gaps ONLY (Mako's words: "median no activity time").
    // null, never NaN, when there are no gaps.
    const sortedGaps = [...gapByIndex.values()].sort((a, b) => a - b);
    const medianGapMins =
      sortedGaps.length > 0
        ? sortedGaps[Math.floor(sortedGaps.length / 2)] // UPPER of the two middle values on even counts
        : null;

    return {
      total,
      withText,
      mediaOnly,
      convos,
      repeats,
      repeatPct,
      fast,
      fastPct,
      counts,
      burstByStart,
      gapByIndex,
      gapCount,
      gapTotalMins,
      clockedMins,
      spanMins,
      medianGapMins,
    };
  }, [messages]);

  if (messages.length === 0) {
    return sessionCount > 0 ? (
      <p className="token-11 text-glass-muted text-center py-8">
        No messages found, but this day recorded {sessionCount} sessions. These conversations may
        have been reassigned since.
      </p>
    ) : (
      <p className="token-11 text-glass-muted text-center py-8">
        No outbound messages in this window.
      </p>
    );
  }

  const {
    total,
    withText,
    mediaOnly,
    convos,
    repeats,
    repeatPct,
    fast,
    fastPct,
    counts,
    burstByStart,
    gapByIndex,
    gapCount,
    gapTotalMins,
    clockedMins,
    spanMins,
    medianGapMins,
  } = stats;

  // Assembled via string parts (not JSX whitespace) so the wrapped "repeats"
  // segment can't drift from the locked copy through JSX text-node collapsing.
  const summaryPrefix = `${total} sent (${withText} with text, ${mediaOnly} media-only) · ${convos} conversations · `;
  const summaryRepeats = `${repeats} repeats (${repeatPct}%)`;
  const summarySuffix = ` · ${fast} sent within 10s of the previous (${fastPct}%)`;

  return (
    <div data-testid="outbound-log">
      <div
        data-testid="outbound-summary"
        className="token-11 text-glass-secondary px-3 py-2 border-b border-black/[0.08]"
      >
        {summaryPrefix}
        <span title="Counts every send of a text that appears more than once, including the first">
          {summaryRepeats}
        </span>
        {summarySuffix}
      </div>
      <div
        data-testid="outbound-reconciliation"
        className="token-11 text-glass-secondary px-3 py-2 border-b border-black/[0.08]"
      >
        {`${formatLag(spanMins)} in the window · ${formatLag(clockedMins)} in session · ${gapCount} no-activity gaps totalling ${formatLag(gapTotalMins)}`}
      </div>
      {medianGapMins !== null && (
        <div data-testid="outbound-median-gap" className="token-11 text-glass-muted px-3 pb-2">
          {`median no activity ${formatLag(medianGapMins)}`}
        </div>
      )}
      <div className="divide-y divide-black/[0.04]">
        {messages.map((m, i) => {
          const burst = burstByStart.get(i);
          const gapMins = gapByIndex.get(i); // MINUTES, or undefined
          const burstLabel = burst
            ? (() => {
                const first = +new Date(messages[burst.start].sent_at);
                const last = +new Date(messages[burst.end].sent_at);
                const secs = Math.round((last - first) / 1000);
                const convoSet = new Set<string>();
                for (let k = burst.start; k <= burst.end; k++) {
                  convoSet.add(messages[k].conversation_id);
                }
                const n = burst.end - burst.start + 1;
                const timeLabel = secs === 0 ? "in under 1s" : `in ${secs}s`;
                const label =
                  convoSet.size === 1
                    ? `${n} messages to 1 conversation ${timeLabel}`
                    : `${n} messages to ${convoSet.size} conversations ${timeLabel}`;
                return label;
              })()
            : null;
          const DIV = "token-10 text-glass-muted px-3 pt-3 pb-1"; // the existing class string, unchanged
          const repeatCount = counts.get(norm(m.message_text)) ?? 0;

          return (
            <React.Fragment key={m.id}>
              {gapMins !== undefined ? (
                <div data-testid="outbound-gap-divider" data-gap-mins={gapMins} className={DIV}>
                  {burstLabel
                    ? `⏸ ${formatLag(gapMins)} no activity · then ${burstLabel}`
                    : `⏸ ${formatLag(gapMins)} no activity`}
                </div>
              ) : burstLabel ? (
                <div data-testid="outbound-burst-header" className={DIV}>
                  {burstLabel}
                </div>
              ) : null}
              <div
                data-testid="outbound-row"
                data-conversation-id={m.conversation_id}
                className="flex items-start gap-3 px-3 py-2"
              >
                <span className="token-9 text-glass-muted shrink-0 pt-0.5">
                  {formatTimeInTZ(m.sent_at, timezone)}
                </span>
                <div className="shrink-0 w-36">
                  {onConversationClick ? (
                    <button
                      type="button"
                      onClick={() =>
                        onConversationClick(
                          m.conversation_id,
                          m.contact_name || "Unknown",
                          m.lead_instagram
                        )
                      }
                      className="token-11 text-glass text-left truncate block w-full hover:bg-black/[0.03] rounded transition-colors"
                    >
                      {m.contact_name || "Unknown"}
                    </button>
                  ) : (
                    <span className="token-11 text-glass truncate block">
                      {m.contact_name || "Unknown"}
                    </span>
                  )}
                  {m.lead_instagram && (
                    <span className="token-10 text-glass-muted truncate block">
                      @{m.lead_instagram}
                    </span>
                  )}
                </div>
                <p className="token-chat text-glass flex-1 min-w-0 whitespace-pre-wrap break-words">
                  {m.message_text !== null ? (
                    m.message_text
                  ) : (
                    <span className="italic opacity-60">
                      {m.has_media ? "[Image sent]" : "[No text]"}
                    </span>
                  )}
                </p>
                {/* One shrink-0 flex around both badges so the columns stay
                    aligned when either is absent. ~Half of all rows carry no
                    lag badge, and that absence is the feature: a send with
                    nothing to answer has no honest number. */}
                <span className="flex items-center gap-2 shrink-0">
                  {repeatCount > 1 && (
                    <span
                      data-testid="outbound-repeat-badge"
                      title={`This exact text was sent ${repeatCount} times in this window`}
                      className="token-10 text-ds-amber"
                    >
                      ↻ {repeatCount}×
                    </span>
                  )}
                  {m.reply_lag_mins != null && (
                    <span
                      data-testid="outbound-lag-badge"
                      data-lag-mins={m.reply_lag_mins}
                      // The NOUN for this raw number is `wait` (Mako, Aug 6 2026). It goes
                      // here on the aria-label, never in the title. The title is a full
                      // sentence with no noun slot and its copy is locked by
                      // P-TIMELINE-REPLY-LATENCY Step 3 — editing it breaks that plan's
                      // shipped tests. Same word, same spelling, in metricMeta.ts,
                      // ReplyTimeline.tsx and SetterMethodologyModal.tsx.
                      aria-label={`Wait: ${formatLag(m.reply_lag_mins)}`}
                      title={`Replied ${formatLag(m.reply_lag_mins)} after the lead's message`}
                      className="token-10 text-ds-accent"
                    >
                      ⏱ {formatLag(m.reply_lag_mins)}
                    </span>
                  )}
                </span>
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};
